import React from 'react';
import {
  AppstoreOutlined,
  BookOutlined,
  CheckSquareOutlined,
  CloseOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  FormOutlined,
} from '@ant-design/icons';
import { useParams, useSearchParams } from 'react-router';
import { Splitter } from 'antd';
import { useEffectWithPrev } from '@owl-frontend/components';
import { createThread } from '../api/agentChat/threads/client';
import useAgentChatThreadsHook from '../data/agentChatThreads/useAgentChatThreadsHook';
import { useSelector } from '../data/store';
import {
  DRAWER_PARAM,
  encodeDrawerItem,
  encodeDrawerStack,
  parseDrawerStack,
} from './parseFileParam';
import { addRecent } from './recentsStorage';
import { useClaim } from './useClaim';
import { useDossierPanelSizes } from './useDossierPanelSizes';
import { useDossierTabs } from './useDossierTabs';
import EscChat from './Chat/Chat';
import useFilesHook from './ClaimOverview/FilesListContainer/useFilesHook';
import { EscDossierContext } from './DossierContext';
import styles from './DossierLayout.module.scss';
import DossierRightPane from './DossierRightPane/DossierRightPane';
import {
  parseSegment,
  SEGMENT_PARAM,
  type SegmentTab,
} from './DossierRightPane/tabs';
import DrawerContent, {
  type DrawerHistoryItem,
} from './DrawerContent/DrawerContent';

function formatDossierName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) {
    return name;
  }
  const last = parts[parts.length - 1];
  const firstInitial = parts[0][0].toUpperCase();
  return `${last}, ${firstInitial}`;
}

const EscDossierLayoutInner: React.FC<{ dossierId: string }> = ({
  dossierId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const claim = useClaim(dossierId);
  const { tabs, ready } = useDossierTabs(dossierId);
  const segment = parseSegment(searchParams, tabs);
  const [{ sizes }, { onSplitterResize }] = useDossierPanelSizes();

  React.useEffect(() => {
    if (!ready) {
      return;
    }
    const raw = searchParams.get(SEGMENT_PARAM);
    if (raw === null || tabs.includes(raw as SegmentTab)) {
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(SEGMENT_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [ready, tabs, searchParams, setSearchParams]);

  const [
    { threads: cachedThreads, hasData: hasThreadsForDossier },
    threadsHandler,
  ] = useAgentChatThreadsHook(dossierId);
  const landingThread = cachedThreads.find((t) => t.scope.length === 0) ?? null;

  const [, filesHandler] = useFilesHook(dossierId);
  const hasFilesForDossier = useSelector(
    (s) => dossierId !== undefined && s.files.files[dossierId] !== undefined
  );
  const filesDispatchedForRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (
      !dossierId ||
      hasFilesForDossier ||
      filesDispatchedForRef.current === dossierId
    ) {
      return;
    }
    filesDispatchedForRef.current = dossierId;
    filesHandler.fetchFiles({ stateDb: true });
  }, [dossierId, hasFilesForDossier, filesHandler]);

  const listDispatchedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (
      !dossierId ||
      hasThreadsForDossier ||
      listDispatchedRef.current === dossierId
    ) {
      return;
    }
    listDispatchedRef.current = dossierId;
    threadsHandler.listThreads();
  }, [dossierId, hasThreadsForDossier, threadsHandler]);

  const landingCreatePendingRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (
      !dossierId ||
      !hasThreadsForDossier ||
      landingCreatePendingRef.current === dossierId
    ) {
      return;
    }
    if (cachedThreads.some((t) => t.scope.length === 0)) {
      return;
    }
    landingCreatePendingRef.current = dossierId;
    (async () => {
      try {
        const created = await createThread(dossierId, []);
        threadsHandler.threadUpserted(created);
      } catch {
        landingCreatePendingRef.current = null;
      }
    })();
  }, [dossierId, hasThreadsForDossier, cachedThreads, threadsHandler]);

  const drawerStack = React.useMemo(
    () => parseDrawerStack(searchParams),
    [searchParams]
  );
  const top =
    drawerStack.length > 0 ? drawerStack[drawerStack.length - 1] : null;
  const drawerOpen = top !== null;

  const activeFile = useSelector((s) =>
    top?.type === 'file' ? s.files.files[dossierId]?.[top.id] ?? null : null
  );

  useEffectWithPrev(
    (prevClaim) => {
      if (claim === null || dossierId == null || prevClaim === claim) {
        return;
      }
      addRecent({
        type: 'dossier',
        id: dossierId,
        dossierId,
        title: claim.dossier_name
          ? formatDossierName(claim.dossier_name)
          : 'Unnamed',
        subtitle: claim.client_dossier_id,
        path: `/claims/${dossierId}`,
        visitedAt: Date.now(),
      });
    },
    [claim, dossierId]
  );

  const writeStack = React.useCallback(
    (
      build: (current: DrawerHistoryItem[]) => DrawerHistoryItem[],
      replace: boolean
    ) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const items = build(parseDrawerStack(prev));
          if (items.length === 0) {
            next.delete(DRAWER_PARAM);
          } else {
            next.set(DRAWER_PARAM, encodeDrawerStack(items));
          }
          return next;
        },
        { replace }
      );
    },
    [setSearchParams]
  );

  const onFileClick = React.useCallback(
    (fileId: string, page?: number) => {
      writeStack((s) => [...s, { type: 'file', id: fileId, page }], false);
    },
    [writeStack]
  );

  const onTaskClick = React.useCallback(
    (taskId: string) => {
      writeStack(() => [{ type: 'task', id: taskId }], false);
    },
    [writeStack]
  );

  const onClaimClick = React.useCallback(
    (claimId: string) => {
      writeStack((s) => [...s, { type: 'claim', id: claimId }], false);
    },
    [writeStack]
  );

  const onWikiClick = React.useCallback(
    (path: string) => {
      writeStack((s) => [...s, { type: 'wiki', path }], false);
    },
    [writeStack]
  );

  const onArtifactClick = React.useCallback(
    (artifactId: string) => {
      writeStack((s) => [...s, { type: 'artifact', id: artifactId }], false);
    },
    [writeStack]
  );

  const onArtifactReplace = React.useCallback(
    (oldArtifactId: string, newArtifactId: string) => {
      writeStack((s) => {
        let idx = -1;
        for (let i = s.length - 1; i >= 0; i--) {
          const item = s[i];
          if (item.type === 'artifact' && item.id === oldArtifactId) {
            idx = i;
            break;
          }
        }
        if (idx >= 0) {
          const next = [...s];
          next[idx] = { type: 'artifact', id: newArtifactId };
          return next;
        }
        return [...s, { type: 'artifact', id: newArtifactId }];
      }, true);
    },
    [writeStack]
  );

  const navigateToHistory = React.useCallback(
    (item: DrawerHistoryItem) => {
      const target = encodeDrawerItem(item);
      writeStack((s) => {
        const idx = s.findIndex((it) => encodeDrawerItem(it) === target);
        return idx >= 0 ? s.slice(0, idx + 1) : s;
      }, true);
    },
    [writeStack]
  );

  const closeOrBack = React.useCallback(() => {
    writeStack((s) => (s.length > 1 ? s.slice(0, -1) : []), true);
  }, [writeStack]);

  React.useEffect(() => {
    if (!drawerOpen) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }
      if (
        document.querySelector(
          '.ant-picker-dropdown:not(.ant-picker-dropdown-hidden), .ant-select-dropdown:not(.ant-select-dropdown-hidden)'
        )
      ) {
        return;
      }
      closeOrBack();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen, closeOrBack]);

  const onThreadGone = React.useCallback(
    (threadId: string) => {
      threadsHandler.threadRemoved(threadId);
      landingCreatePendingRef.current = null;
    },
    [threadsHandler]
  );

  // Prefill the chat composer (e.g. from a task suggestion button); the user
  // reviews and sends. The token makes each request distinct so repeat clicks
  // re-trigger the prefill effect.
  const [pendingPrefill, setPendingPrefill] = React.useState<{
    token: number;
    mode: 'message';
    text: string;
  } | null>(null);
  const onComposeMessage = React.useCallback((text: string) => {
    setPendingPrefill((prev) => ({
      token: (prev?.token ?? 0) + 1,
      mode: 'message',
      text,
    }));
  }, []);

  const contextValue = React.useMemo(
    () => ({
      claim,
      onFileClick,
      onDirectiveClick: () => {},
      onClaimClick,
      onWikiClick,
      onArtifactClick,
      onArtifactReplace,
      onTaskClick,
      onComposeMessage,
    }),
    [
      claim,
      onFileClick,
      onClaimClick,
      onWikiClick,
      onArtifactClick,
      onArtifactReplace,
      onTaskClick,
      onComposeMessage,
    ]
  );

  const overlayKind =
    top?.type === 'file'
      ? activeFile?.file_type === 'ACTIVITY'
        ? { icon: <FormOutlined />, label: 'Note preview' }
        : activeFile
        ? { icon: <FileTextOutlined />, label: 'File preview' }
        : { icon: <FileTextOutlined />, label: 'Preview' }
      : top?.type === 'task'
      ? { icon: <CheckSquareOutlined />, label: 'Task' }
      : top?.type === 'claim'
      ? { icon: <FolderOpenOutlined />, label: 'Claim preview' }
      : top?.type === 'wiki'
      ? { icon: <BookOutlined />, label: 'Wiki preview' }
      : { icon: <AppstoreOutlined />, label: 'Artifact preview' };

  return (
    <EscDossierContext.Provider value={contextValue}>
      <div className={styles.container}>
        <div className={styles.layoutBody}>
          {dossierId && (
            <Splitter className={styles.splitter} onResize={onSplitterResize}>
              <Splitter.Panel defaultSize={`${sizes.left}%`} min="20%">
                {landingThread && (
                  <EscChat
                    key={landingThread.thread_id}
                    dossierId={dossierId}
                    threadId={landingThread.thread_id}
                    onThreadGone={onThreadGone}
                    pendingPrefill={pendingPrefill}
                    onPrefillConsumed={() => setPendingPrefill(null)}
                  />
                )}
              </Splitter.Panel>
              <Splitter.Panel min="20%">
                <div className={styles.companion}>
                  <DossierRightPane
                    dossierId={dossierId}
                    segment={segment}
                    onFileClick={onFileClick}
                  />
                  {drawerOpen && (
                    <div className={styles.overlay}>
                      <div className={styles.overlayBar}>
                        <span className={styles.overlayBarLabel}>
                          {overlayKind.icon}
                          {overlayKind.label}
                        </span>
                        <button
                          type="button"
                          className={styles.overlayClose}
                          onClick={closeOrBack}
                          aria-label="Close"
                        >
                          <CloseOutlined />
                        </button>
                      </div>
                      <div className={styles.overlayBody}>
                        <DrawerContent
                          fileId={top?.type === 'file' ? top.id : null}
                          filePage={top?.type === 'file' ? top.page : undefined}
                          taskId={top?.type === 'task' ? top.id : null}
                          claimId={top?.type === 'claim' ? top.id : null}
                          artifactId={top?.type === 'artifact' ? top.id : null}
                          wikiPath={top?.type === 'wiki' ? top.path : null}
                          history={drawerStack}
                          onNavigateHistory={navigateToHistory}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </Splitter.Panel>
            </Splitter>
          )}
        </div>
      </div>
    </EscDossierContext.Provider>
  );
};

const EscDossierLayout: React.FC = () => {
  const { dossierId } = useParams<{ dossierId: string }>();
  if (!dossierId) {
    return null;
  }
  return <EscDossierLayoutInner dossierId={dossierId} />;
};

export default EscDossierLayout;
